import { Link } from "react-router-dom";
import { ArrowRight, Star, Shield, Heart, Users, CheckCircle2, Sparkles, MapPin } from "lucide-react";
import TestimonialsCarousel from "@/components/TestimonialsCarousel";
import { useLang } from "@/context/LangContext";

const differentiators = [
  { title: "Precision Match™", tag: "Signature System", desc: "We don't just fill positions. We match personality, values and lifestyle — engineered for long-term harmony." },
  { title: "Matching Score™", tag: "Beyond the Résumé", desc: "A proprietary compatibility score covering skills, temperament, communication and adaptability." },
  { title: "All-Filipino Consultant Team", tag: "20+ Years Deep", desc: "Native cultural insight that produces deeper interviews and truer matches." },
  { title: "365-Day Employer Care™", tag: "After Placement", desc: "A full year of guidance so both employer and helper build a working relationship that lasts." },
];

const services = [
  { title: "Cooking", tag: "Good Food. Happy Family.", desc: "Experienced helpers with the skills to prepare meals your family will love." },
  { title: "Housekeeping", tag: "A Clean Home. A Clear Mind.", desc: "Helpers who keep your home clean, organised and cared for every day." },
  { title: "Special Needs Care", tag: "Care Beyond Routine.", desc: "Experience, patience and compassion for families with special needs." },
];

export default function Home() {
  const { t } = useLang();
  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#F9F9F7]">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 pt-16 pb-20 sm:pt-24 sm:pb-28 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7 reveal">
            <div className="overline mb-6">{t("home.overline")}</div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] text-[#0A1128] mb-6">
              {t("home.hero1")}<br/>
              <span className="italic text-[#688A4E]">{t("home.hero2")}</span> {t("home.hero3")}<br/>
              {t("home.hero4")}
            </h1>
            <p className="text-lg text-gray-700 max-w-xl leading-relaxed mb-10">{t("home.subhead")}</p>
            <div className="flex flex-wrap gap-4">
              <Link to="/search-maid" className="btn-primary" data-testid="hero-find-helper">
                {t("cta.findHelper")} <ArrowRight className="w-4 h-4"/>
              </Link>
              <Link to="/application" className="btn-outline" data-testid="hero-apply">
                {t("cta.submitApp")}
              </Link>
            </div>

            <div className="mt-14 flex items-center gap-8 flex-wrap">
              <div className="flex items-center gap-2">
                <div className="flex text-[#688A4E]">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current"/>)}
                </div>
                <div className="text-sm text-gray-600"><span className="font-semibold text-[#0A1128]">4.3</span> · {t("home.reviews")}</div>
              </div>
              <div className="h-6 w-px bg-gray-300"/>
              <div className="text-sm text-gray-600">{t("home.years")}</div>
            </div>
          </div>

          <div className="lg:col-span-5 relative reveal">
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_notes-to-site/artifacts/84dl4s8g_Helper_infant_stroller.jpeg"
                alt="Happy Home helper with baby in a family home"
                className="w-full h-[520px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-white border border-gray-200 p-6 max-w-[260px] shadow-xl">
                <div className="overline mb-2">Matching Score™</div>
                <div className="font-serif text-3xl text-[#0A1128] mb-1">96 <span className="text-lg text-[#688A4E]">/ 100</span></div>
                <div className="text-xs text-gray-500">Maria Santos · Infant care · 8 yrs experience</div>
              </div>
              <div className="absolute -top-6 -right-6 bg-[#0A1128] text-white p-6 max-w-[220px]">
                <div className="overline text-[#A1A9BC] mb-2">Located at</div>
                <div className="text-sm leading-relaxed flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-[#688A4E]"/>Lucky Plaza · Orchard Road</div>
              </div>
            </div>
          </div>
        </div>

        {/* Trust ribbon */}
        <div className="border-t border-b border-gray-200 bg-white py-6 overflow-hidden">
          <div className="marquee font-serif italic text-2xl text-gray-400">
            {["Peace of Mind", "Precision Match", "Trusted Since 2006", "365-Day Care", "Building Happy Homes", "The Right Person, Not Just a Helper"].concat(
              ["Peace of Mind", "Precision Match", "Trusted Since 2006", "365-Day Care", "Building Happy Homes", "The Right Person, Not Just a Helper"]
            ).map((t, i) => (
              <span key={i} className="shrink-0">{t} <span className="text-[#688A4E] mx-4">✦</span></span>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-24 sm:py-32 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <div className="overline mb-4">Our Philosophy</div>
          <h2 className="font-serif text-4xl sm:text-5xl text-[#0A1128] leading-tight divider-serif">We match people, not profiles.</h2>
        </div>
        <div className="lg:col-span-8 space-y-6 text-lg text-gray-700 leading-relaxed">
          <p>Every family is different. Some need someone gentle with elderly parents. Some need someone energetic with young children. Some need an excellent cook. Others simply want someone reliable who will stay for years.</p>
          <p>We take the time to understand your family's routines, expectations, communication style and priorities before recommending candidates.</p>
          <p className="font-serif italic text-2xl text-[#0A1128]">Because the best helper isn't the one with the longest résumé. It's the one who fits your family.</p>
        </div>
      </section>

      {/* Peace of Mind Bullets */}
      <section className="bg-[#0A1128] text-white grain relative">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-24 sm:py-32 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="overline text-[#688A4E] mb-4">We Sell Peace of Mind</div>
            <h2 className="font-serif text-4xl sm:text-5xl leading-tight mb-8">You're choosing someone who will become part of your family's daily life.</h2>
            <p className="text-[#A1A9BC] text-lg leading-relaxed max-w-lg">You want someone who will care with compassion, keep your children safe, be trustworthy, stay committed, and fit your home's values.</p>
          </div>
          <ul className="space-y-6">
            {[
              { icon: Heart, text: "Care for elderly parents with compassion" },
              { icon: Shield, text: "Keep your baby safe and nurtured" },
              { icon: Users, text: "Carefully matched to your family's personality" },
              { icon: CheckCircle2, text: "Thoroughly screened for reliability and commitment" },
            ].map((item, i) => (
              <li key={i} className="flex gap-5 items-start border-b border-white/10 pb-5">
                <div className="w-11 h-11 border border-[#688A4E] flex items-center justify-center shrink-0">
                  <item.icon className="w-5 h-5 text-[#688A4E]"/>
                </div>
                <div className="text-lg pt-2">{item.text}</div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Differentiators */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-24 sm:py-32">
        <div className="max-w-2xl mb-16">
          <div className="overline mb-4">Why Families Choose Us</div>
          <h2 className="font-serif text-4xl sm:text-5xl text-[#0A1128] leading-tight">Creating happy homes starts with the right person.</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-px bg-gray-200 border border-gray-200">
          {differentiators.map((d, i) => (
            <div key={i} className="bg-white p-10 hover:bg-[#F9F9F7] transition group cursor-default">
              <div className="flex items-start justify-between mb-6">
                <Sparkles className="w-8 h-8 text-[#688A4E]"/>
                <div className="text-xs tracking-[0.2em] uppercase text-gray-400">0{i+1}</div>
              </div>
              <div className="overline mb-3 text-gray-400">{d.tag}</div>
              <h3 className="font-serif text-3xl text-[#0A1128] mb-4 group-hover:text-[#688A4E] transition">{d.title}</h3>
              <p className="text-gray-600 leading-relaxed">{d.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Services preview */}
      <section className="bg-[#F9F9F7]">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-24 sm:py-32">
          <div className="flex flex-col md:flex-row items-end justify-between gap-6 mb-16">
            <div>
              <div className="overline mb-4">What We Offer</div>
              <h2 className="font-serif text-4xl sm:text-5xl text-[#0A1128] leading-tight">Services, tailored to your household.</h2>
            </div>
            <Link to="/services" className="text-sm hh-underline pb-1 flex items-center gap-2 text-[#688A4E]" data-testid="home-view-all-services">
              Explore all services <ArrowRight className="w-4 h-4"/>
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {services.map((s, i) => (
              <div key={i} className="bg-white border border-gray-200 p-10 hover:-translate-y-1 hover:shadow-xl transition">
                <div className="text-6xl font-serif text-[#E8ECE1] mb-6">0{i+1}</div>
                <div className="overline mb-2">{s.tag}</div>
                <h3 className="font-serif text-2xl text-[#0A1128] mb-3">{s.title}</h3>
                <p className="text-gray-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Warmth gallery */}
      <section className="bg-white" data-testid="warmth-gallery">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-24 sm:py-32">
          <div className="mb-14 max-w-2xl">
            <div className="overline mb-4">Life at Happy Home</div>
            <h2 className="font-serif text-4xl sm:text-5xl text-[#0A1128] leading-tight">Moments that make a house <span className="italic text-[#688A4E]">a home</span>.</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 sm:gap-5">
            {[
              { src: "https://customer-assets.emergentagent.com/job_notes-to-site/artifacts/l99lo6ux_Helper_Side_Infant%20and%20Child_Living%20Room.jpeg", cap: "Play & learn" },
              { src: "https://customer-assets.emergentagent.com/job_notes-to-site/artifacts/xmup6ulo_Helper_front_infant.jpeg", cap: "Newborn care" },
              { src: "https://customer-assets.emergentagent.com/job_notes-to-site/artifacts/r353n6m2_Helper_maroonshirt_kitchen.jpeg", cap: "Home cooking" },
              { src: "https://customer-assets.emergentagent.com/job_notes-to-site/artifacts/1yoqkael_Helper_Duo.jpeg", cap: "Immaculate homes" },
              { src: "https://customer-assets.emergentagent.com/job_notes-to-site/artifacts/793yn23u_WhatsApp%20Image%202026-07-04%20at%202.12.53%20AM.jpeg", cap: "Elderly companionship" },
            ].map((g, i) => (
              <figure key={i} className="group relative overflow-hidden aspect-[3/4]">
                <img src={g.src} alt={g.cap} className="w-full h-full object-cover transition duration-700 group-hover:scale-105"/>
                <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent text-white p-4 text-sm font-serif italic">{g.cap}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial carousel */}
      <TestimonialsCarousel />

      {/* CTA */}
      <section className="bg-[#0A1128] text-white grain">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-24 text-center">
          <div className="overline text-[#688A4E] mb-6 justify-center">{t("home.ctaOverline")}</div>
          <h2 className="font-serif text-4xl sm:text-6xl leading-tight max-w-3xl mx-auto mb-10">{t("home.ctaTitle")}</h2>
          <div className="flex justify-center gap-4 flex-wrap">
            <Link to="/application" className="btn-primary" data-testid="home-cta-apply">{t("cta.startApp")} <ArrowRight className="w-4 h-4"/></Link>
            <Link to="/contact" className="btn-outline-light" data-testid="home-cta-contact">{t("cta.speakConsultant")}</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
